/**
 * La cámara del teléfono como lector de códigos de barras.
 *
 * Two readers, one interface. Chrome on Android ships one, backed by Google Play services:
 * fast, sure of itself at an angle, and nothing to download. Safari on an iPhone has none, and
 * neither does Chrome on a laptop, so they get ZXing compiled to WebAssembly — the same reader
 * the ponyfill wraps in the standard `BarcodeDetector` shape, so the loop that feeds frames to
 * it never knows which one it has.
 *
 * **The WebAssembly ships inside the app.** By default the ponyfill fetches it from a CDN the
 * first time it is needed, which is exactly what a shop with no signal cannot reach, and it
 * would be the one part of the till that needed the internet. Imported with `?url`, Vite gives
 * it a hashed name under `/assets/`, and the service worker precaches it with everything else.
 *
 * The pure half — which pixels to read, which reads to believe — is `lecturas.ts`.
 */

import type { BarcodeDetector as DetectorWasm } from 'barcode-detector/ponyfill';
import lectorWasm from 'zxing-wasm/reader/zxing_reader.wasm?url';

/**
 * What a shop's shelves carry: EAN and UPC on anything sold retail, Code 128 on labels a shop
 * or a distributor prints, ITF on the carton a case comes in.
 *
 * Nothing else, on purpose. Every format the reader tries is one more chance to find a
 * barcode in the pattern on a wrapper, and no product carries a QR code for its price.
 */
export const FORMATOS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'itf'] as const;

export interface Detector {
  detect(imagen: HTMLCanvasElement): Promise<{ rawValue: string; format: string }[]>;
}

export type Motor = 'nativo' | 'wasm';

interface ClaseNativa {
  new (opciones: { formats: string[] }): Detector;
  getSupportedFormats(): Promise<readonly string[]>;
}

/** One object for the life of the page: the ponyfill compares it to decide whether to recompile. */
const AJUSTES_WASM = {
  locateFile: (ruta: string, prefijo: string) => (ruta.endsWith('.wasm') ? lectorWasm : prefijo + ruta),
};

/**
 * The phone's own reader when it has one that knows retail codes; the bundled one otherwise.
 *
 * Resolves once the reader can actually read — compiling the WebAssembly takes a moment on a
 * cheap phone, and the screen says so instead of looking at a barcode and doing nothing.
 */
export async function crearDetector(forzar?: Motor): Promise<{ detector: Detector; motor: Motor }> {
  const Nativo = (globalThis as { BarcodeDetector?: ClaseNativa }).BarcodeDetector;
  if (Nativo && forzar !== 'wasm') {
    try {
      const soportados = await Nativo.getSupportedFormats();
      const formatos = FORMATOS.filter((f) => soportados.includes(f));
      if (formatos.includes('ean_13')) return { detector: new Nativo({ formats: formatos }), motor: 'nativo' };
    } catch {
      // A reader the browser advertises and cannot start: the bundled one will do.
    }
  }

  const { BarcodeDetector, prepareZXingModule } = await import('barcode-detector/ponyfill');
  await prepareZXingModule({ overrides: AJUSTES_WASM, fireImmediately: true });
  const detector: DetectorWasm = new BarcodeDetector({ formats: [...FORMATOS] });
  return { detector, motor: 'wasm' };
}

export type FallaCamara = 'permiso' | 'sinCamara' | 'ocupada' | 'lector' | 'otra';

/** What went wrong, in terms of what somebody can do about it. */
export function clasificarFalla(e: unknown): FallaCamara {
  const nombre = e instanceof Error || e instanceof DOMException ? e.name : '';
  switch (nombre) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'permiso';
    case 'NotFoundError':
    case 'OverconstrainedError':
    case 'NotSupportedError':
      return 'sinCamara';
    case 'NotReadableError':
    case 'AbortError':
      return 'ocupada';
    default:
      return 'otra';
  }
}

/**
 * The camera on the back, at a resolution a barcode reads well at.
 *
 * 1280×720 is asked for, not demanded: across the viewfinder that is about eight pixels to
 * each bar of an EAN-13, plenty, and more only makes every frame slower to read on the phones
 * this runs on. Continuous focus is requested where the camera lists it; most focus on their
 * own anyway, and a camera that ignores the request still works.
 */
export async function abrirCamara(): Promise<MediaStream> {
  const medios = navigator.mediaDevices;
  if (!medios?.getUserMedia) throw new DOMException('Sin cámara', 'NotSupportedError');

  const stream = await medios.getUserMedia({
    audio: false,
    video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
  });

  const pista = stream.getVideoTracks()[0];
  try {
    const capacidades = pista?.getCapabilities?.() as { focusMode?: string[] } | undefined;
    if (pista && capacidades?.focusMode?.includes('continuous')) {
      await pista.applyConstraints({ advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet] });
    }
  } catch {
    // Focus it cannot be asked about is focus it already manages.
  }
  return stream;
}

/** Whether this camera has a light the page can switch on. */
export function tieneLinterna(stream: MediaStream): boolean {
  const pista = stream.getVideoTracks()[0];
  const capacidades = pista?.getCapabilities?.() as { torch?: boolean } | undefined;
  return capacidades?.torch === true;
}

export async function ponerLinterna(stream: MediaStream, encendida: boolean): Promise<void> {
  await stream.getVideoTracks()[0]?.applyConstraints({
    advanced: [{ torch: encendida } as MediaTrackConstraintSet],
  });
}

let hayCamaraPromesa: Promise<boolean> | null = null;

/**
 * Whether this device has a camera to scan with.
 *
 * Asked of the device list, which answers without asking permission: a laptop with no camera
 * does not get a button that can only ever fail. Asked once per page.
 */
export function hayCamara(): Promise<boolean> {
  hayCamaraPromesa ??= (async () => {
    try {
      const medios = navigator.mediaDevices;
      if (!medios?.getUserMedia || !medios.enumerateDevices) return false;
      return (await medios.enumerateDevices()).some((d) => d.kind === 'videoinput');
    } catch {
      return false;
    }
  })();
  return hayCamaraPromesa;
}
